import {Input} from '@main/components/form/form-field/input';
import {getFormattedFileSize} from '@main/utils/get-formatted-file-size';

/** Компонент выбора файла. */
export class FileInput extends Input {
  /**
   * Создает компонент выбора файла.
   *
   * @param {object} props Параметры компонента.
   * @param {HTMLElement} props.container Родительский элемент.
   * @param {HTMLInputElement} props.input Элемент ввода.
   */
  constructor(props) {
    super(props);
    this.findElements();

    this.maxSize = this.input.dataset.maxSize;
    this.isMultiple = this.el.classList.contains('is-multiple');

    // Таймер для отсрочки очистки ошибки
    this.errorTimeout = null;
  }

  /**
   * Ищет элементы.
   *
   * @returns {void}
   */
  findElements() {
    this.fileNameCaptionNode = this.el.querySelector('.js-form-field-file-caption');
    this.deleteInput = this.el.querySelector('.js-form-field-delete');
    this.fileBlockElement = this.input.closest('.js-file-block');
    this.addButton = this.el.querySelector('.js-add-file');
    this.fieldRowTemplate = this.fileBlockElement?.cloneNode(true);
  }

  /**
   * Инициализация событий.
   *
   * @returns {void}
   */
  bindEventListeners() {
    super.bindEventListeners();

    document.addEventListener('click', (e) => {
      // Проверяем, что клик был по кнопке добавления файла в нашем контейнере
      if (e.target.closest('.js-add-file') && e.target.closest('.js-form-field-file') === this.el) {
        if (this.el.classList.contains('is-multiple')) {
          this.addFileField();
        }
      }

      // Обработчик удаления файла
      if (e.target.classList.contains('js-file-delete') || e.target.closest('.js-file-delete')) {
        const deleteButton = e.target.classList.contains('js-file-delete')
          ? e.target
          : e.target.closest('.js-file-delete');
        const field = deleteButton.closest('.js-form-field-file');

        if (field === this.el) {
          this.deleteFile(e);
        }
      }
    });

    // Для одиночного режима - слушаем изменения input
    if (!this.isMultiple) {
      this.input.addEventListener('change', this.onChange.bind(this));
    }

    // Инициализация файловых блоков
    this.initializeFileBlocks();
  }

  initializeFileBlocks() {
    this.fileBlocks = this.el.querySelectorAll('.js-file-block');
    this.fieldRowTemplate = this.fileBlocks[0]?.cloneNode(true);

    this.fileBlocks.forEach((block) => {
      const input = block.querySelector('.js-form-field-input');
      if (input) {
        input.addEventListener('change', (e) => {
          this.onFileInputChange(e, block);
        });
      }
    });

    this.updateAllDeleteButtons();
  }

  /**
   * Обработчик изменения input для множественного режима.
   *
   * @param {Event} e Событие изменения.
   * @param {HTMLElement} fileBlock Блок файла.
   * @returns {void}
   */
  onFileInputChange(e, fileBlock) {
    const input = e.target;
    const file = input.files[0];
    const caption = fileBlock.querySelector('.js-form-field-file-caption');

    // Очищаем предыдущую ошибку
    this.hideError(fileBlock);

    if (file) {
      // Валидация типа файла
      if (!this.isValidFileType(file)) {
        this.showErrorWithClear('Допустимые форматы: PDF, DOC, DOCX', fileBlock);
        return;
      }

      // Валидация размера файла
      if (!this.isValidFileSize(file)) {
        const maxSizeMB = this.maxSize || 2;
        this.showErrorWithClear(`Файл слишком большой (максимум ${maxSizeMB} МБ)`, fileBlock);
        return;
      }

      fileBlock.classList.add('filled');
      this.hideError(fileBlock);

      if (caption) {
        caption.textContent = file.name;
      }
    } else if (caption) {
      caption.textContent = 'Прикрепить файл (до 2 Мб)';
      fileBlock.classList.remove('filled');
    }

    this.updateAllDeleteButtons();
  }

  /**
   * Показывает ошибку и очищает файл через задержку.
   *
   * @param {string} message Текст ошибки.
   * @param {HTMLElement} fileBlock Блок файла.
   * @returns {void}
   */
  showErrorWithClear(message, fileBlock) {
    this.showError(message, fileBlock);

    // Очищаем предыдущий таймер
    if (this.errorTimeout) {
      clearTimeout(this.errorTimeout);
    }

    // Устанавливаем таймер для очистки файла через 3 секунды
    this.errorTimeout = setTimeout(() => {
      this.clearFileBlock(fileBlock);
      this.errorTimeout = null;
    }, 3000);
  }

  /**
   * Добавляет новое поле для файла.
   *
   * @returns {void}
   */
  addFileField() {
    if (!this.fieldRowTemplate || !this.addButton) {
      return;
    }

    // Клонируем шаблон
    const newFileBlock = this.fieldRowTemplate.cloneNode(true);

    // Очищаем значения
    const input = newFileBlock.querySelector('.js-form-field-input');
    const caption = newFileBlock.querySelector('.js-form-field-file-caption');

    if (input) {
      input.value = '';
      input.addEventListener('change', (e) => this.onFileInputChange(e, newFileBlock));
    }

    if (caption) {
      caption.textContent = 'Прикрепить файл (до 2 Мб)';
    }

    const deleteButton = newFileBlock.querySelector('.js-file-delete');
    if (deleteButton) {
      deleteButton.addEventListener('click', (e) => this.deleteFileBlock(e, newFileBlock));
    }

    // Вставляем перед кнопкой добавления
    this.addButton.parentNode.insertBefore(newFileBlock, this.addButton);

    // Обновляем список блоков
    this.fileBlocks = this.el.querySelectorAll('.js-file-block');

    // Обновляем кнопки удаления
    this.updateAllDeleteButtons();

    // Фокусируемся на новом поле
    if (input) {
      input.click();
    }
  }

  /**
   * Удаляет блок файла.
   *
   * @param {Event} e Событие клика.
   * @param {HTMLElement} fileBlock Блок файла для удаления.
   * @returns {void}
   */
  deleteFileBlock(e, fileBlock) {
    e.stopPropagation();

    const currentBlocks = this.el.querySelectorAll('.js-file-block');
    if (currentBlocks.length <= 1) {
      // Если это последний блок, просто очищаем его
      this.clearFileBlock(fileBlock);
      return;
    }

    // Удаляем блок
    fileBlock.remove();

    // Обновляем список блоков
    this.fileBlocks = this.el.querySelectorAll('.js-file-block');

    // Обновляем кнопки удаления
    this.updateAllDeleteButtons();
  }

  /**
   * Обновляет состояние всех кнопок удаления.
   *
   * @returns {void}
   */
  updateAllDeleteButtons() {
    const currentBlocks = this.el.querySelectorAll('.js-file-block');

    currentBlocks.forEach((block, index) => {
      const deleteButton = block.querySelector('.js-file-delete');
      if (deleteButton) {
        const hasFile = block.classList.contains('filled');

        // Показываем кнопку удаления если:
        // 1. Есть больше одного блока (множественный режим)
        // 2. ИЛИ есть файл в блоке (одиночный режим)
        if (currentBlocks.length > 1 || hasFile) {
          deleteButton.classList.remove('d-none');
        } else {
          deleteButton.classList.add('d-none');
        }
      }
    });

    // Показываем/скрываем кнопку добавления только в множественном режиме
    if (this.addButton && this.isMultiple) {
      this.addButton.classList.remove('d-none');
    }
  }

  /**
   * Коллбек, который обрабатывает выбор файла (для одиночного режима).
   *
   * @returns {void}
   */
  onChange() {
    const [file] = this.input.files;

    // Очищаем предыдущую ошибку
    this.hideError();

    if (file) {
      // Валидация типа файла
      if (!this.isValidFileType(file)) {
        this.showErrorWithClear('Допустимые форматы: PDF, DOC, DOCX', this.fileBlockElement);
        return;
      }

      // Валидация размера файла
      if (!this.isValidFileSize(file)) {
        const maxSizeMB = this.maxSize || 2;
        this.showErrorWithClear(`Файл слишком большой (максимум ${maxSizeMB} МБ)`, this.fileBlockElement);
        return;
      }

      this.fileBlockElement.classList.add('filled');

      // Если все проверки пройдены
      this.hideError();
      this.setCaption(file?.name, getFormattedFileSize(file.size));
      if (this.deleteInput) {
        this.deleteInput.value = '';
      }

      // Обновляем кнопку удаления при выборе файла
      this.updateAllDeleteButtons();
    }
  }

  /**
   * Проверяет допустимость типа файла.
   *
   * @param {File} file Файл для проверки.
   * @returns {boolean} true если тип допустим.
   */
  isValidFileType(file) {
    const allowedTypes = [
      'application/pdf',
      'application/msword',
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
    ];

    const allowedExtensions = ['.pdf', '.doc', '.docx'];

    // Проверка по MIME-type
    if (allowedTypes.includes(file.type)) {
      return true;
    }

    // Дополнительная проверка по расширению
    const fileName = file.name.toLowerCase();
    return allowedExtensions.some((ext) => fileName.endsWith(ext));
  }

  /**
   * Проверяет размер файла.
   *
   * @param {File} file Файл для проверки.
   * @returns {boolean} true если размер допустим.
   */
  isValidFileSize(file) {
    const maxSize = +this.maxSize * 1024 * 1024; // MB в байтах
    return file.size <= maxSize;
  }

  /**
   * Показывает сообщение об ошибке.
   *
   * @param {string} message Текст ошибки.
   * @param {HTMLElement} [fileBlock] Блок файла для отображения ошибки.
   * @returns {void}
   */
  showError(message, fileBlock = null) {
    const targetBlock = fileBlock || this.fileBlockElement;
    targetBlock.classList.add('error');

    const errorCaption = targetBlock.querySelector('.js-form-field-file-caption');
    if (errorCaption) {
      errorCaption.textContent = message;
    }
    this.updateAllDeleteButtons();
  }

  /**
   * Скрывает сообщение об ошибке.
   *
   * @param {HTMLElement} [fileBlock] Блок файла для скрытия ошибки.
   * @returns {void}
   */
  hideError(fileBlock = null) {
    const targetBlock = fileBlock || this.fileBlockElement;
    targetBlock.classList.remove('error');

    const errorCaption = targetBlock.querySelector('.js-form-field-file-caption');
    if (errorCaption) {
      errorCaption.textContent = 'Прикрепить файл (до 2 Мб)';
    }
  }

  /**
   * Очищает поле файла при ошибке валидации.
   *
   * @param {HTMLElement} [fileBlock] Блок файла для очистки.
   * @returns {void}
   */
  clearFile(fileBlock = null) {
    const targetBlock = fileBlock || this.fileBlockElement;
    const input = targetBlock.querySelector('.js-form-field-input');
    const caption = targetBlock.querySelector('.js-form-field-file-caption');

    if (input) input.value = '';
    if (caption) caption.textContent = 'Прикрепить файл (до 2 Мб)';
    targetBlock.classList.remove('filled');

    // Сбрасываем скрытое поле для удаления
    if (this.deleteInput) {
      this.deleteInput.value = '';
    }

    this.hideError(targetBlock);

    // Обновляем кнопку удаления после очистки
    this.updateAllDeleteButtons();
  }

  /**
   * Очищает блок файла.
   *
   * @param {HTMLElement} fileBlock Блок файла для очистки.
   * @returns {void}
   */
  clearFileBlock(fileBlock) {
    this.clearFile(fileBlock);
  }

  /**
   * Установить имя файла и размер.
   *
   * @returns {void}
   */
  setCaption(name, size) {
    if (this.fileNameCaptionNode) {
      this.fileNameCaptionNode.innerHTML = name ? `${name}` : '';
    }
  }

  /**
   * Получить значение файла.
   *
   * @returns {File|File[]} Файл или массив файлов.
   */
  getValue() {
    if (this.isMultiple) {
      const files = [];
      const inputs = this.el.querySelectorAll('.js-form-field-input');

      inputs.forEach((input) => {
        if (input.files.length > 0) {
          files.push(input.files[0]);
        }
      });

      return files;
    }

    return this.input.files[0];
  }

  /**
   * Удаляет выбранный файл из поля ввода файла.
   *
   * @param {Event} e — Объект события, вызванный нажатием кнопки удаления.
   * @return {void}
   */
  deleteFile(e) {
    e.stopPropagation();
    const fileClearButton = e.target.classList.contains('js-file-delete')
      ? e.target
      : e.target.closest('.js-file-delete');
    const field = fileClearButton.closest('.js-file-block');

    this.clearFile(field);
  }
}
